import { useMemo } from 'react';
import { Modal, Form, Select, DatePicker, Input, Alert, Tag, Space, Spinner, Typography, Button, message } from '../../ui';
import dayjs from 'dayjs';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useConfirmedMutation } from '../../lib/confirm';
import { request } from '../../api/client';
import { requestAllPages } from '../../api/allPages';
import { useAuthStore } from '../../store/auth';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Prefill the absent SP (e.g. opened from a "needs coverage" row). */
  presetAbsentId?: string;
  /** Prefill the coverage window (e.g. chained from marking a leave range). */
  presetValidFrom?: string;
  presetValidTo?: string;
}

const cap = (s?: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : '');

/**
 * Grant a coverer time-boxed access to an absent Sales Person's territories. No ownership
 * changes — customers stay with the original SP; the coverer just gains visit/order/create
 * access for the window. Auto-expires when the window ends.
 */
export function GrantCoverageModal({ open, onClose, presetAbsentId, presetValidFrom, presetValidTo }: Props) {
  const [form] = Form.useForm();
  const qc = useQueryClient();
  const myRole = useAuthStore((s) => s.user?.role);
  const myId = useAuthStore((s) => s.user?.id);

  const { data: spUsers } = useQuery({
    queryKey: ['sp-list-all'],
    queryFn: () => requestAllPages('/users', { role: 'SALES_PERSON' }),
    enabled: open,
  });
  const absentId = Form.useWatch('absentUserId', form);

  // The exact territories the coverer will take over = the absent SP's own (non-coverage)
  // assignments, deduped by territory — mirrors what grantCoverage copies server-side.
  const { data: terrData, isFetching: loadingTerrs } = useQuery({
    queryKey: ['user-territories', absentId],
    queryFn: () => request<any[]>({ url: `/territory-assignments/user/${absentId}` }),
    enabled: open && !!absentId,
  });
  const coveredTerrs = useMemo(() => {
    const seen = new Set<string>();
    return (terrData ?? [])
      .filter((t: any) => t.kind !== 'COVERAGE')
      .filter((t: any) => { const k = String(t.territoryId); if (seen.has(k)) return false; seen.add(k); return true; });
  }, [terrData]);

  // A Sales Head only sees their own team; admins see all active SPs.
  const team = useMemo(() => {
    let list: any[] = (spUsers?.data ?? []).filter((u: any) => u.status === 'active');
    if (myRole === 'SALES_HEAD') list = list.filter((u) => String(u.reportsTo) === String(myId));
    return list;
  }, [spUsers, myRole, myId]);
  const absentOptions = team.map((u) => ({ value: String(u.id), label: u.fullName }));
  const covererOptions = team.filter((u) => String(u.id) !== String(absentId)).map((u) => ({ value: String(u.id), label: u.fullName }));

  // Seeding is via the Form's initialValues + a per-preset key (below), not a setFieldsValue open-effect
  // that races the Radix-modal remount (which would blank the prefilled Absent SP + date range).
  const grant = useConfirmedMutation({ title: "Grant this coverage?", content: "The user starts covering the selected territories immediately.", okText: "Yes, grant" }, {
    mutationFn: (vals: any) => {
      const [f, t] = vals.range ?? [];
      if (!f || !t) return Promise.reject(new Error('Pick both a start and end date for the coverage period'));
      return request({
        url: '/territory-assignments/coverage', method: 'POST',
        data: {
          absentUserId: vals.absentUserId,
          covererUserId: vals.covererUserId,
          validFrom: f.format('YYYY-MM-DD'),
          validTo: t.format('YYYY-MM-DD'),
          note: vals.note,
        },
      });
    },
    onSuccess: (res: any) => {
      const n = res?.granted ?? 0;
      message.success(n ? `Coverage granted over ${n} territor${n === 1 ? 'y' : 'ies'}` : 'Nothing to cover — that Sales Person has no territories');
      qc.invalidateQueries({ queryKey: ['territory-coverage-grants'] });
      qc.invalidateQueries({ queryKey: ['coverage-needed'] });
      qc.invalidateQueries({ queryKey: ['badges'] });
      onClose();
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to grant coverage'),
  });

  return (
    <Modal
      open={open}
      onOpenChange={(o) => { if (!o) onClose(); }}
      title="Assign coverage"
      footer={<><Button variant="default" onClick={onClose}>Cancel</Button><Button variant="primary" loading={grant.isPending} onClick={() => form.submit()}>Grant coverage</Button></>}
    >
      <Form
        key={`cov-${presetAbsentId ?? 'new'}-${presetValidFrom ?? ''}-${presetValidTo ?? ''}`}
        form={form}
        layout="vertical"
        initialValues={{
          absentUserId: presetAbsentId,
          range: presetValidFrom && presetValidTo ? [dayjs(presetValidFrom), dayjs(presetValidTo)] : undefined,
        }}
        onFinish={(v) => grant.mutate(v)}
      >
        <Form.Item name="absentUserId" label="Absent Sales Person" rules={[{ required: true, message: 'Pick the absent sales person' }]}>
          <Select showSearch optionFilterProp="label" placeholder="Who is away?"
            notFoundContent={myRole === 'SALES_HEAD' ? 'No sales persons in your team' : 'None found'}
            options={absentOptions} onChange={() => form.setFieldsValue({ covererUserId: undefined })} />
        </Form.Item>
        {absentId && (
          <Form.Item label="Territories to be covered" style={{ marginBottom: 16 }}>
            {loadingTerrs ? (
              <Spinner />
            ) : coveredTerrs.length ? (
              <Space size={[6, 6]} wrap>
                {coveredTerrs.map((t: any) => (
                  <Tag key={t.territoryId} tone="blue" style={{ marginInlineEnd: 0 }}>
                    {t.name}
                    <span style={{ opacity: 0.6, marginLeft: 5, fontSize: 11 }}>{cap(t.level)}</span>
                  </Tag>
                ))}
              </Space>
            ) : (
              <Typography.Text type="secondary">This Sales Person owns no territories — nothing to cover.</Typography.Text>
            )}
          </Form.Item>
        )}
        <Form.Item name="covererUserId" label="Cover with" rules={[{ required: true, message: 'Pick who will cover' }]}>
          <Select showSearch optionFilterProp="label" placeholder="Who will stand in?"
            notFoundContent="No other sales persons" options={covererOptions} disabled={!absentId} />
        </Form.Item>
        <Form.Item name="range" label="Coverage period" rules={[{ required: true, message: 'Pick the date range' }]}>
          <DatePicker.RangePicker className="w-full" format="DD MMM YYYY"
            disabledDate={(d) => d && d < dayjs().startOf('day')} />
        </Form.Item>
        <Form.Item name="note" label="Note (optional)">
          <Input.TextArea rows={2} placeholder="e.g. medical leave, covering Pune East" />
        </Form.Item>
        <Alert type="info"
          title="The coverer gains access to the absent SP's territories for the period only. Customers stay owned by the original SP; coverage ends automatically." />
      </Form>
    </Modal>
  );
}
